import React from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Eye, X, CheckCircle2, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

/**
 * Signal Copilot action bar — v2.6.
 * Three per-user actions on any signal:
 *   Watch  → add to default market watchlist + subscribe to lifecycle
 *   Skip   → hide from Investigation Queue, record rejection
 *   Follow → soft "I'd take this trade" marker (no execution)
 */
const ACTIONS = [
  { key: "watch",  label: "Watch",  icon: Eye,          color: "emerald",
    tip: "Add this market to your watchlist and track lifecycle changes." },
  { key: "skip",   label: "Skip",   icon: X,            color: "zinc",
    tip: "Hide this signal from your feed and remember the rejection." },
  { key: "follow", label: "Follow", icon: CheckCircle2, color: "amber",
    tip: "Mark this as one you'd take. No execution — just a personal record." },
];

const ACTIVE_CLS = {
  emerald: "bg-emerald-500/20 border-emerald-500/40 text-emerald-200",
  amber:   "bg-amber-500/20 border-amber-500/40 text-amber-200",
  zinc:    "bg-zinc-700/40 border-zinc-500/60 text-zinc-200",
};

export default function SignalActionBar({
  signalId,
  currentAction,
  size = "md",
  testid = "signal-action-bar",
  onChange,
}) {
  const qc = useQueryClient();
  const setMut = useMutation({
    mutationFn: async (action) => {
      if (currentAction === action) {
        // Toggle off
        await api.delete(`/copilot/actions/${signalId}`);
        return { action: null };
      }
      const r = await api.post("/copilot/actions", { signal_id: signalId, action });
      return r.data;
    },
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["signal", signalId] });
      qc.invalidateQueries({ queryKey: ["investigation-queue"] });
      qc.invalidateQueries({ queryKey: ["copilot-actions"] });
      onChange?.(r.action);
      if (r.action) {
        toast.success(`Marked as ${r.action}`);
      } else {
        toast.info("Action cleared");
      }
    },
    onError: (e) => {
      toast.error(e?.response?.data?.detail || "Action failed");
    },
  });

  const pending = setMut.isPending;

  const btnSize = size === "sm" ? "h-7 px-2 text-[11px]" : "h-8 px-3 text-xs";

  return (
    <div className="flex flex-wrap gap-1.5" data-testid={testid}>
      {ACTIONS.map(({ key, label, icon: Icon, color, tip }) => {
        const active = currentAction === key;
        return (
          <button
            key={key}
            onClick={() => setMut.mutate(key)}
            disabled={pending}
            title={tip}
            data-testid={`${testid}-${key}`}
            aria-pressed={active}
            className={`inline-flex items-center gap-1 rounded-md border transition-all ${btnSize} ${
              active
                ? ACTIVE_CLS[color]
                : "bg-zinc-900 border-zinc-700 text-zinc-300 hover:bg-zinc-800 hover:border-zinc-500"
            } ${pending ? "opacity-50 cursor-wait" : "cursor-pointer"}`}
          >
            {pending && currentAction === null && setMut.variables === key ? (
              <Loader2 size={11} className="animate-spin" />
            ) : (
              <Icon size={11} />
            )}
            {label}
          </button>
        );
      })}
    </div>
  );
}
